import { AppContext } from '@/ui/components/Context';
import { showCustomDialog } from '@juvare/common-web-ui/DialogHelper';
import { useIdleTimeout } from '@juvare/common-web-ui/hooks';
import { useUserClient } from '@juvare/platform-sdk-react/ui/hooks';
import { Button, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

const WARNING_TIMEOUT_MS = 60 * 1000;
const SESSION_REFRESH_MS = 5 * 60 * 1000;

interface LogoutWarningContentProps {
    close: (closed?: () => void) => void;
    onIdleCancel: () => void;
    idleForMs: number;
    maxIdleMs: number;
}

const LogoutWarningContent: React.FC<LogoutWarningContentProps> = ({ close, onIdleCancel, idleForMs, maxIdleMs }) => {
    const [idleMs, setIdleMs] = React.useState(idleForMs);
    const userClient = useUserClient();
    const appContext = React.useContext(AppContext);
    const { t } = useTranslation();
    const loggedOut = idleMs >= maxIdleMs;
    React.useEffect(() => {
        const handle = setTimeout(() => {
            if (!loggedOut) {
                setIdleMs((prevState) => prevState + 1000);
            }
        }, 1000);
        return () => {
            clearTimeout(handle);
        };
    }, [idleMs, loggedOut]);
    return (
        <>
            <DialogTitle>
                {!loggedOut && t('idleMonitor.continueWorking')}
                {loggedOut && t('idleMonitor.sessionExpired')}
            </DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {!loggedOut && t('idleMonitor.doYouWantToContinue', { item: Math.round((maxIdleMs - idleMs) / 1000) })}
                    {loggedOut && t('idleMonitor.loggedOut')}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                {!loggedOut && (
                    <>
                        <Button
                            color='primary'
                            onClick={() => {
                                onIdleCancel();
                                userClient
                                    .renewSession()
                                    .then(() => undefined)
                                    .catch(() => appContext.initState())
                                    .finally(() => close());
                            }}
                        >
                            {t('idleMonitor.continue')}
                        </Button>
                        <Button
                            color='primary'
                            onClick={() => {
                                userClient.logout().finally(() =>
                                    close(() => {
                                        window.location.reload();
                                    }),
                                );
                            }}
                        >
                            {t('idleMonitor.logOut')}
                        </Button>
                    </>
                )}
                {loggedOut && (
                    <Button
                        color='primary'
                        onClick={() =>
                            close(() => {
                                window.location.reload();
                            })
                        }
                    >
                        {t('idleMonitor.ok')}
                    </Button>
                )}
            </DialogActions>
        </>
    );
};

export function useIdleUserMonitor(): void {
    const appContext = React.useContext(AppContext);
    const userClient = useUserClient();
    const sessionTimeoutMs = appContext.currentUser ? appContext.currentUser.sessionTimeoutInSeconds * 1000 : -1;
    useIdleTimeout({
        idleTimeoutMs: sessionTimeoutMs - WARNING_TIMEOUT_MS,
        debounceMs: SESSION_REFRESH_MS,
        onIdle: (idleForMs, onIdleCancel) => {
            showCustomDialog({
                content: (close) => (
                    <LogoutWarningContent close={close} onIdleCancel={onIdleCancel} idleForMs={idleForMs} maxIdleMs={sessionTimeoutMs} />
                ),
            });
        },
        onActive: () =>
            userClient
                .renewSession()
                .then(() => undefined)
                .catch(() => undefined),
    });
}
